import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  generatePartnerToken,
  listPartnerTokens,
  revokePartnerToken,
  deletePartnerToken,
} from '@/services/partnerAdmin';
import type { BusinessVisibility } from '@/types';

// ===========================================
// Query Keys
// ===========================================

export const partnerTokenKeys = {
  all: ['partner-tokens'] as const,
  list: (companyId?: string) => [...partnerTokenKeys.all, 'list', companyId] as const,
};

// ===========================================
// Query Hooks
// ===========================================

/**
 * Fetch partner tokens, optionally filtered by company
 */
export function usePartnerTokens(companyId?: string, includeRevoked: boolean = false) {
  return useQuery({
    queryKey: partnerTokenKeys.list(companyId),
    queryFn: async () => {
      const result = await listPartnerTokens(companyId, includeRevoked);
      if (!result.success) throw new Error(result.error);
      return result.tokens || [];
    },
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

// ===========================================
// Mutation Hooks
// ===========================================

/**
 * Generate a new partner token (active until revoked)
 */
export function useGeneratePartnerToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      companyName,
      businessVisibility,
    }: {
      companyId: string;
      companyName: string;
      businessVisibility: BusinessVisibility;
    }) => {
      const result = await generatePartnerToken(
        companyId,
        companyName,
        businessVisibility
      );
      if (!result.success) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      // Invalidate token lists
      queryClient.invalidateQueries({ queryKey: partnerTokenKeys.all });
    },
  });
}

/**
 * Revoke a partner token
 */
export function useRevokePartnerToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tokenId: string) => {
      const result = await revokePartnerToken(tokenId);
      if (!result.success) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      // Invalidate token lists
      queryClient.invalidateQueries({ queryKey: partnerTokenKeys.all });
    },
  });
}

/**
 * Delete a revoked partner token
 */
export function useDeletePartnerToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tokenId: string) => {
      const result = await deletePartnerToken(tokenId);
      if (!result.success) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      // Invalidate token lists
      queryClient.invalidateQueries({ queryKey: partnerTokenKeys.all });
    },
  });
}
